import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import DataTable, { ColumnDef } from "@/components/utils/DataTable";
import styles from "@/components/utils/shared.module.css";
import { PageFrame, PageHeader, Surface, Button, Pill } from '@/components/ui';
import { marginCalculationAPI, PeakMarginScheme, PeakMarginResponse } from "@/services/api";
import { formatCurrency, formatDate, CurrencyCode } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";

type NonNriPurchaseData = {
  client_master__scheme: number;
  client_master__scheme__code?: string;
  total_amount: number;
  margin_amount: number;
};

type MarginCalculationResponse = {
  order_date: string;
  margin_percentage: number;
  non_nri_order_data: NonNriPurchaseData[];
};

const inr = (value: number) => formatCurrency(value, CurrencyCode.INR, 0);

const MarginCalculation: React.FC = () => {
  const [data, setData] = useState<MarginCalculationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marginPercent, setMarginPercent] = useState(20);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [peakMargin, setPeakMargin] = useState<PeakMarginResponse | null>(null);
  const [peakLoading, setPeakLoading] = useState(true);
  const [peakError, setPeakError] = useState<string | null>(null);
  const [peakRefreshTrigger, setPeakRefreshTrigger] = useState(0);
  // User-entered peak margin values, keyed by scheme id (kept as raw input strings).
  const [enteredMargins, setEnteredMargins] = useState<Record<number, string>>({});
  const [savingPeak, setSavingPeak] = useState(false);
  const [copyingPeak, setCopyingPeak] = useState(false);

  // Non-NRI margin: fetched on mount and only when Refresh is clicked (applying the
  // current margin %). Changing the input alone does NOT trigger a fetch.
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await marginCalculationAPI.getMarginCalculation({ margin_percentage: marginPercent });
        if (!cancelled) setData(response);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to fetch margin calculation.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
    // marginPercent is intentionally omitted — a new value applies only on Refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Peak margin: does not depend on the margin % input. Loads on mount and after a save.
  useEffect(() => {
    let cancelled = false;
    const fetchPeakMargin = async () => {
      try {
        setPeakLoading(true);
        setPeakError(null);
        const response = await marginCalculationAPI.getPeakMargin();
        if (cancelled) return;
        setPeakMargin(response);
        // Prefill the editable inputs with any previously stored values.
        const prefilled: Record<number, string> = {};
        response.schemes.forEach(scheme => {
          prefilled[scheme.scheme_id] =
            scheme.stored_peak_margin != null ? String(scheme.stored_peak_margin) : "";
        });
        setEnteredMargins(prefilled);
      } catch (err: any) {
        if (!cancelled) setPeakError(err?.message || "Failed to fetch peak margin.");
      } finally {
        if (!cancelled) setPeakLoading(false);
      }
    };
    fetchPeakMargin();
    return () => {
      cancelled = true;
    };
  }, [peakRefreshTrigger]);

  const handleMarginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) setMarginPercent(value);
  };

  const handleEnteredMarginChange = useCallback((schemeId: number, value: string) => {
    setEnteredMargins(prev => ({ ...prev, [schemeId]: value }));
  }, []);

  // A value is only saved if it parses and is non-negative — applied to typed input and to
  // values copied from the last working day alike, so a bad stored value is never resaved.
  const isSavableAmount = (amount: number) => !isNaN(amount) && amount >= 0;

  // Single POST path shared by "Save" and "Copy from last working day"; `setBusy` is the
  // caller's own flag so only the button that was clicked shows a pending label.
  const savePeakMargins = async (
    entries: Array<{ scheme_id: number; amount: number }>,
    setBusy: (busy: boolean) => void
  ) => {
    try {
      setBusy(true);
      const result = await marginCalculationAPI.savePeakMargin({ schemes: entries });
      toast.success(`Saved peak margin for ${result.saved} scheme(s).`);
      setPeakRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      toast.error(err?.message || "Failed to save peak margin.");
    } finally {
      setBusy(false);
    }
  };

  const handleSavePeakMargins = async () => {
    const entries = Object.entries(enteredMargins)
      .map(([schemeId, raw]) => ({ scheme_id: Number(schemeId), amount: parseFloat(raw) }))
      .filter(entry => isSavableAmount(entry.amount));

    if (entries.length === 0) {
      toast.warn("Enter a peak margin value for at least one scheme.");
      return;
    }

    await savePeakMargins(entries, setSavingPeak);
  };

  // Carry the last working day's saved margins into today and store them straight away.
  // The inputs stay editable afterwards, so the user can adjust any scheme and save again.
  const handleCopyFromLastWorkingDay = async () => {
    const entries = (peakMargin?.schemes ?? [])
      .filter(scheme => scheme.previous_peak_margin != null)
      .map(scheme => ({
        scheme_id: scheme.scheme_id,
        amount: Number(scheme.previous_peak_margin),
      }))
      .filter(entry => isSavableAmount(entry.amount));

    if (entries.length === 0) {
      toast.warn("No peak margin was saved on the last working day.");
      return;
    }

    setEnteredMargins(prev => {
      const next = { ...prev };
      entries.forEach(entry => {
        next[entry.scheme_id] = String(entry.amount);
      });
      return next;
    });

    await savePeakMargins(entries, setCopyingPeak);
  };

  const handleRefresh = () => setRefreshTrigger(prev => prev + 1);

  const marginColumns: ColumnDef<NonNriPurchaseData>[] = useMemo(() => [
    {
      key: "scheme",
      header: "Scheme",
      align: "left",
      render: row => row.client_master__scheme__code || `Scheme ${row.client_master__scheme}`,
      renderFooter: () => <strong>Total</strong>,
    },
    {
      key: "total_amount",
      header: "Total Amount",
      align: "right",
      render: row => inr(row.total_amount),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => r.total_amount))}</strong>,
    },
    {
      key: "margin_amount",
      header: "Margin Amount",
      align: "right",
      render: row => inr(row.margin_amount),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => r.margin_amount))}</strong>,
    },
  ], []);

  const peakColumns: ColumnDef<PeakMarginScheme>[] = useMemo(() => [
    {
      key: "scheme",
      header: "Scheme",
      align: "left",
      render: row => row.scheme_code || row.scheme_name || `Scheme ${row.scheme_id}`,
      renderFooter: () => <strong>Total</strong>,
    },
    {
      key: "buy_amount",
      header: "Buy Amount",
      align: "right",
      render: row => inr(Number(row.buy_amount)),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => Number(r.buy_amount)))}</strong>,
    },
    {
      key: "sell_amount",
      header: "Sell Amount",
      align: "right",
      render: row => inr(Number(row.sell_amount)),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => Number(r.sell_amount)))}</strong>,
    },
    {
      key: "available_cash",
      header: "Available Cash",
      align: "right",
      render: row => inr(Number(row.available_cash)),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => Number(r.available_cash)))}</strong>,
    },
    {
      key: "peak_margin",
      header: "Peak Margin",
      align: "right",
      render: row => inr(Number(row.peak_margin)),
      renderFooter: rows => <strong>{inr(sumBy(rows, r => Number(r.peak_margin)))}</strong>,
    },
    {
      key: "set_peak_margin",
      header: "Set Peak Margin (₹)",
      align: "right",
      render: row => (
        <input
          type="number"
          min={0}
          step="any"
          value={enteredMargins[row.scheme_id] ?? ""}
          onChange={e => handleEnteredMarginChange(row.scheme_id, e.target.value)}
          className={styles["cell-input"]}
          style={{ width: 130, height: 32, textAlign: "right" }}
          placeholder="Enter amount"
        />
      ),
    },
  ], [enteredMargins, handleEnteredMarginChange]);

  const hasPreviousPeakMargins = useMemo(
    () =>
      (peakMargin?.schemes ?? []).some(
        scheme => scheme.previous_peak_margin != null && isSavableAmount(Number(scheme.previous_peak_margin))
      ),
    [peakMargin]
  );

  const marginMeta = data ? (
    <>
      Order date <strong>{formatDate(data.order_date)}</strong>
      <Pill tone="info" plain>{data.margin_percentage}%</Pill>
    </>
  ) : undefined;

  const peakMeta = peakMargin ? (
    <>
      {peakMargin.current_date && <>Date <strong>{formatDate(peakMargin.current_date)}</strong></>}
      <Pill tone="info" plain>{peakMargin.margin_percentage}%</Pill>
    </>
  ) : undefined;

  const peakActions = peakMargin ? (
    <>
      <Button
        size="sm"
        onClick={handleCopyFromLastWorkingDay}
        disabled={copyingPeak || savingPeak || peakLoading || !hasPreviousPeakMargins}
        title={
          hasPreviousPeakMargins
            ? "Copy the last working day's peak margin into today and save it."
            : 'No peak margin was saved on the last working day.'
        }
      >
        {copyingPeak
          ? 'Copying…'
          : `Copy from ${peakMargin.previous_margin_date ? formatDate(peakMargin.previous_margin_date) : 'last working day'}`}
      </Button>
      <Button
        size="sm"
        variant="primary"
        onClick={handleSavePeakMargins}
        disabled={savingPeak || copyingPeak || peakLoading}
      >
        {savingPeak ? 'Saving…' : 'Save Peak Margin'}
      </Button>
    </>
  ) : undefined;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Margin Calculation"
          subtitle="Margin required against the day's purchases, and the peak margin set per scheme."
          actions={
            <>
              <label htmlFor="margin-input" className={styles["inline-field"]}>
                <span>Margin (%)</span>
                <input
                  id="margin-input"
                  type="number"
                  min={0}
                  step={0.1}
                  value={marginPercent}
                  onChange={handleMarginChange}
                  className={styles.input}
                  style={{ width: 92 }}
                />
              </label>
              <Button onClick={handleRefresh} disabled={loading}>
                Refresh
              </Button>
            </>
          }
        />
      }
    >
      {/* Non-NRI margin */}
      <Surface title="Non-NRI Margin Calculation" meta={marginMeta}>
        <DataTable<NonNriPurchaseData>
          embedded
          data={data?.non_nri_order_data ?? []}
          columns={marginColumns}
          rowKey={row => row.client_master__scheme}
          totalCount={data?.non_nri_order_data.length ?? 0}
          loading={loading}
          error={error}
          currentPage={1}
          totalPages={1}
          onPageChange={() => undefined}
          pageSize={1}
          sortField=""
          sortDir="asc"
          onSort={() => undefined}
          hideSearch
          compact
          loadingMessage="Loading margin calculation data…"
          emptyMessage="No margin data found."
        />
      </Surface>

      {/* Peak margin per scheme */}
      <Surface title="Peak Margin by Scheme" meta={peakMeta} actions={peakActions}>
          <DataTable<PeakMarginScheme>
            embedded
            data={peakMargin?.schemes ?? []}
            columns={peakColumns}
            rowKey={row => row.scheme_id}
            totalCount={peakMargin?.schemes.length ?? 0}
            loading={peakLoading}
            error={peakError}
            currentPage={1}
            totalPages={1}
            onPageChange={() => undefined}
            pageSize={1}
            sortField=""
            sortDir="asc"
            onSort={() => undefined}
            hideSearch
            compact
            loadingMessage="Loading peak margin data…"
            emptyMessage="No peak margin data found."
          />
      </Surface>
    </PageFrame>
  );
};

export default MarginCalculation;
